import type { TFunction } from 'i18next';
import type { ImportFit, ImportFitReason, ImportPreview } from '@/api/imports';
import { formatCivilDate } from '@/i18n/format';
import { CANONICAL_FIELDS, type CanonicalField } from './mapping';

const KNOWN: ReadonlySet<string> = new Set([
  'MAPPED_COLUMN_MISSING',
  'NO_ROWS',
  'ROWS_REJECTED',
  'DATES_IMPLAUSIBLE',
  'BALANCE_MISMATCH',
  'SIGN_INVERTED',
  'ALL_BEFORE_OPENING',
  'BEFORE_OPENING',
  'BALANCE_ROWS',
]);

/** The reasons about lines on or before the account's opening (TMMAPI-0176 §H). */
export const BEFORE_OPENING_CODES: ReadonlySet<string> = new Set([
  'ALL_BEFORE_OPENING',
  'BEFORE_OPENING',
]);

const FIELDS: ReadonlySet<string> = new Set(CANONICAL_FIELDS);

export type FitView = 'results' | 'alreadyImported' | 'misfit';

/**
 * Which view a verdict opens. POOR wins; all duplicates is not a misfit (TMMAPI-0149 9a). Nothing
 * new is the served total (`newCount`, earlier history included; TMMAPI-0191).
 */
export function fitView(fit: ImportFit, preview: ImportPreview): FitView {
  if (fit.status === 'POOR') return 'misfit';
  if (preview.newCount === 0 && preview.duplicates > 0) return 'alreadyImported';
  return 'results';
}

/**
 * One plain sentence per fit reason (TMMAPP-0107 §2, TMMAPI-0149 9a), from the catalog: `count` and
 * `detail` are interpolated, the missing column by its field label. A code this build doesn't know
 * reads as the generic sentence. `ALL_BEFORE_OPENING` names the opening date (`detail`, a civil
 * date, in `locale`); without one it reads as `BEFORE_OPENING` (TMMAPP-0139 item 8).
 */
export function fitReasonText(t: TFunction, reason: ImportFitReason, locale = 'en'): string {
  if (!KNOWN.has(reason.code)) return t('imports.fit.reason.UNKNOWN');
  if (BEFORE_OPENING_CODES.has(reason.code)) {
    if (reason.count === null) return t('imports.fit.reason.UNKNOWN');
    return reason.code === 'ALL_BEFORE_OPENING' && reason.detail !== null
      ? t('imports.fit.reason.ALL_BEFORE_OPENING', {
          count: reason.count,
          date: formatCivilDate(reason.detail, locale),
        })
      : t('imports.fit.reason.BEFORE_OPENING', { count: reason.count });
  }
  if (reason.code === 'MAPPED_COLUMN_MISSING') {
    const column =
      reason.detail !== null && FIELDS.has(reason.detail)
        ? t(`imports.field.${reason.detail as CanonicalField}`)
        : (reason.detail ?? '');
    return column.length > 0
      ? t('imports.fit.reason.MAPPED_COLUMN_MISSING', { column })
      : t('imports.fit.reason.UNKNOWN');
  }
  if (reason.code === 'ROWS_REJECTED') {
    return reason.count !== null
      ? t('imports.fit.reason.ROWS_REJECTED', { count: reason.count })
      : t('imports.fit.reason.ROWS_REJECTED_SOME');
  }
  // TMMAPI-0178: a statement's balance rows ("Beginning balance as of …") are skipped, never row
  // errors. Good to know, not a misfit: the server grades the fit (TMMAPP-0140 item 4).
  if (reason.code === 'BALANCE_ROWS') {
    return reason.count !== null
      ? t('imports.fit.reason.BALANCE_ROWS', { count: reason.count })
      : t('imports.fit.reason.BALANCE_ROWS_SOME');
  }
  return t(`imports.fit.reason.${reason.code}`);
}
